import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import FLAG from 'config/flag.singleton';
import DELAY from 'constants/delay.const';
import ELID from 'constants/elid.const';
import S from 'constants/string.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonBetaAnnualSalon } from 'selectors/currentSalon.selectors';
import { fromEventToValue } from 'utils/converters/event.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import determineActiveSelection from 'utils/dom/document/determineActiveSelection.util';
import call from 'utils/functions/call.util';
import delayed from 'utils/functions/delayed.util';
import { modifyUrl } from 'utils/links/modify.util';
import logger from 'utils/logger.util';
import checkAboveGsm7Bit from 'utils/strings/checkAboveGsm7Bit.util';

import Checkbox from 'settings/page-elements/dropdown/Checkbox';

import CN from './SmsComposeAndSend.module.scss';

const L = logger('SmsComposeAndSend');

const USE_SPAM_LOG = fromAnyToBool(FLAG.loggerSpam);

const LINK_SMS_TPL = 'https://help.getslick.com/en/articles/8601866-sms-marketing-templates';

const SmsComposeAndSend = ({
  ButtonText,
  appointmentId = null,
  bucketType,
  closePopup,
  consultation_link: link,
  currClientList: recipients,
  getUniqClients,
  has_billing: hasBilling,
  hasBetaAnnualSalon,
  messageSent,
  ncobTinyUrl: url,
  purchaseModal,
  salonId,
  setSmsMessage,
  showHideElmClass = '',
  showPopup,
  smsMessage,
  title,
}) => {
  const { val: isTipVisible, nay: hideTip } = useTip(true);
  const { val: isLimitReached, set: setLimitReached } = useTip(false);
  const { val: hasConsultation, tip: toggleConsultation } = useTip(false);
  const { val: hasOnlineLink, tip: toggleOnlineLink } = useTip(false);
  const { val: hasClientAgreed, set: handleClientAgreed } = useTip(false);

  const [bgn, setBgn] = useStateWhileMounted(0);
  const [end, setEnd] = useStateWhileMounted(0);
  const [sms, setSms] = useStateWhileMounted(fromAnyToString(smsMessage));
  const [count, setCount] = useStateWhileMounted(1);

  const { t } = useTranslation();

  const updateText = useCallback(
    (text) => {
      const len = fromAnyToString(text).length;

      setLimitReached(len >= 670);

      if (!hasBilling && !hasBetaAnnualSalon) {
        call('SmsComposeAndSend.closePopup', closePopup, 'smsComposeModalOpen');
        call('SmsComposeAndSend.showPopup', showPopup, 'billingPopupOpen');
        return;
      }

      const limit = checkAboveGsm7Bit({ text, strat: 'utf16' }) ? 67 : 153;
      const c = Math.ceil(len / limit);

      const element = document.getElementById(ELID.marketingSms);
      const result = determineActiveSelection({ element });

      setSms(text);
      setCount(c);
      setBgn(result.bgn);
      setEnd(result.end);

      call('SmsComposeAndSend.setSmsMessage', setSmsMessage, text, c);
    },
    [
      setLimitReached,
      hasBilling,
      hasBetaAnnualSalon,
      setSms,
      setCount,
      setBgn,
      setEnd,
      setSmsMessage,
      closePopup,
      showPopup,
    ],
  );

  const handleClose = useCallback(
    // NOTE: no action on click close if purchase Modal is open
    () => void (!purchaseModal && !messageSent && call('SmsComposeAndSend.showPopup', showPopup, 'exitPopupOpen')),
    [messageSent, purchaseModal, showPopup],
  );

  const insertOnlineBooking = useCallback(() => {
    const tinyUrl = modifyUrl({ url, addParams: { utm: 'sms' }, delProtocol: true });
    const infix = `${t('scheduler.smsButton.bookVia')} ${tinyUrl}`;
    const prefix = sms.substring(0, bgn);
    const suffix = sms.substring(bgn, sms.length);

    const text = hasOnlineLink ? sms.replace(infix, '') : `${prefix} ${infix} ${suffix} `;

    updateText(text);
    setSms(text);
    toggleOnlineLink();
  }, [url, t, sms, bgn, hasOnlineLink, updateText, setSms, toggleOnlineLink]);

  const insertConsultationForm = useCallback(() => {
    const infix = fromAnyToString(link).replace(/(^\w+:|^)\/\//, '');
    const prefix = sms.substring(0, bgn);
    const suffix = sms.substring(bgn, sms.length);

    const text = hasConsultation ? sms.replace(infix, '') : `${prefix} ${infix} ${suffix} `;

    updateText(text);
    setSms(text);
    toggleConsultation();
  }, [bgn, hasConsultation, link, sms, setSms, toggleConsultation, updateText]);

  const handleSend = useCallback(() => {
    if (sms === '') return;

    closePopup('smsComposeModalOpen');

    // eslint-disable-next-line no-nested-ternary
    const bucket = bucketType === 'first' ? 'first_time' : bucketType === 'list' ? 'all' : bucketType;
    const appointment = appointmentId || void 1;
    const id = salonId.toString();

    const data = { recipients, text: sms, numOfMessages: count, bucket, appointment };
    L.unignore('sendSmsClick()', 'getUniqClients:', getUniqClients(data, id));

    showPopup('beforeSendPopupOpen');
  }, [appointmentId, bucketType, closePopup, getUniqClients, sms, count, recipients, salonId, showPopup]);

  const handleText = useCallback((ev) => void updateText(fromEventToValue(ev)), [updateText]);

  useEffect(() => void delayed(DELAY.autohide2, hideTip), [updateText, hideTip]);

  if (USE_SPAM_LOG) L.debug('()', { bgn, end, sms, smsMessage });

  return (
    <div data-bem="SmsComposeAndSend" data-dialog-body="SmsComposeAndSend">
      <div className="compose-container">
        <div className="compose-header">
          <h2>{title}</h2>
          <div className="close-icon" role="button" tabIndex={0} onClick={handleClose} style={{ cursor: 'pointer' }} />
        </div>
        <div className="compose-text-area-container">
          <textarea
            id={ELID.marketingSms}
            className="marketing-sms"
            placeholder={t('scheduler.smsButton.messagePlaceholder')}
            value={sms}
            onChange={handleText}
            onClick={handleText}
          />

          <hr />
          <div className="m1m2-container">
            <div className="m1">
              <p>{t('scheduler.smsButton.usingMessages', { count })}</p>
              <div className="num-message-info">
                <span className="tooltip">
                  <div className="tooltip-centered">{t('scheduler.smsButton.typeMessage')}</div>
                </span>
              </div>
            </div>
            <div className="m2">
              {link && (
                <button className="btn btn-outline" onClick={insertConsultationForm}>
                  {hasConsultation
                    ? t('scheduler.smsButton.removeConsultationLink')
                    : t('scheduler.smsButton.addConsultationLink')}
                </button>
              )}

              <button className="btn btn-outline" onClick={insertOnlineBooking}>
                {hasOnlineLink ? t('scheduler.smsButton.removeBookingLink') : t('scheduler.smsButton.addBookingLink')}
              </button>
            </div>
          </div>
          {isTipVisible && (
            <div className="tooltip-opened animated-slow fadeInDown">
              {t('scheduler.smsButton.includeName')}
              {S.space}
              <span role="img" aria-labelledby="business name">
                {S.smile}
              </span>
            </div>
          )}
        </div>
        <div className="compose-footer-container">
          <div
            className={cn({
              'disclaimer-container': true,
              [showHideElmClass]: showHideElmClass,
            })}
          >
            {isLimitReached && <p className="char-limit">{t('scheduler.smsButton.maxCharacters')}</p>}
            {isLimitReached && <p className="char-limit">{t('scheduler.smsButton.shortenMessage')}</p>}
            <div>
              <label htmlFor="checkBox">
                <Checkbox isChecked={hasClientAgreed} id={salonId.toString()} onCheckboxClick={handleClientAgreed} />
              </label>
              <p>{t('scheduler.smsButton.agreeTerms')}</p>
            </div>
            <section
              className={cn({
                'role-banner no-margin': true,
                [showHideElmClass]: showHideElmClass,
              })}
            >
              <div className="role-banner-text">
                <span className="emoji" role="img" aria-labelledby="lamp">
                  {S.lamp}
                </span>
                <div>
                  <strong>{t('scheduler.smsButton.suggestion')}</strong> {t('scheduler.smsButton.templates')}
                  <ExternalLink variant="inline" link={LINK_SMS_TPL}>
                    <div className={CN.link}>{t('scheduler.smsButton.viewTemplatesButton')}</div>
                  </ExternalLink>
                </div>
              </div>
              <div className="role-banner-text" style={{ marginTop: '12px' }}>
                <span className="emoji" role="img" aria-labelledby="lamp">
                  {S.lamp}
                </span>
                <div>
                  <strong>{t('scheduler.smsButton.maxSms')} </strong>
                  {t('scheduler.smsButton.emojis')} {S.shocked}
                </div>
              </div>
            </section>
          </div>
          <div
            data-bem="SmsComposeAndSend__buttons"
            className={cn({
              'dialog-footer-marketing': true,
              [showHideElmClass]: showHideElmClass,
            })}
          >
            {hasClientAgreed ? (
              // NOTE: from marketing clients
              <Button variant="primaryForce" onClick={handleSend} disabled={!hasClientAgreed || !sms} height="max">
                {ButtonText}
              </Button>
            ) : (
              // NOTE: from appointment card
              <Button variant="primaryForce" onClick={handleSend} disabled={!sms} height="max">
                {ButtonText}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const SmsComposeAndSendHoc = connect((state) => ({
  hasBetaAnnualSalon: selectCurrentSalonBetaAnnualSalon(state),
}))(SmsComposeAndSend);

export default SmsComposeAndSendHoc;
